import { type Dispatch, type SetStateAction, useState } from 'react';

import { Flex } from 'components/layout';
import { Maybe } from 'types/UtilityTypes';
import {
  GenericSuggestion,
  SuggestionPipelines,
} from 'types/suggestions/CoreSuggestions';

import type { AppointmentDisplayTimezones } from '../utils/suggestionFormatters';
import {
  SuggestionCategory,
  SuggestionCategoryProps,
} from './SuggestionCategory';

type GroupedByPipeline = Partial<
  Record<SuggestionPipelines, GenericSuggestion[]>
>;

export type SuggestionListProps = {
  additionalLoadBuildingSuggestions?: SuggestionCategoryProps['additionalSuggestions'];
  pipelinesToShow: SuggestionPipelines[];
  groupedSuggestions: GroupedByPipeline;
  clickedSuggestion: Maybe<GenericSuggestion>;
  expandedPipeline: Maybe<SuggestionPipelines>;
  setExpandedPipeline: Dispatch<SetStateAction<Maybe<SuggestionPipelines>>>;
  visibleCountByPipeline: Partial<Record<SuggestionPipelines, number>>;
  setVisibleCountByPipeline: Dispatch<
    SetStateAction<Partial<Record<SuggestionPipelines, number>>>
  >;
  isBatchQuoteEnabled: boolean;
  // Kit <-> Batch Quote selection sync (suggestion ids currently in the batch).
  batchSelectedSuggestionIds: number[];
  handleApplySuggestion: (suggestion: GenericSuggestion) => void;
  handleApplyBatch: (suggestions: GenericSuggestion[]) => void;
  handleDismissSuggestion: (suggestion: GenericSuggestion) => void;
  appointmentTimezones?: AppointmentDisplayTimezones;
  tmsName?: Maybe<string>;
};

// Collapsible column of {@link SuggestionCategory} sections (one per pipeline).
export function SuggestionList({
  additionalLoadBuildingSuggestions,
  pipelinesToShow,
  groupedSuggestions,
  clickedSuggestion,
  expandedPipeline,
  setExpandedPipeline,
  visibleCountByPipeline,
  setVisibleCountByPipeline,
  isBatchQuoteEnabled,
  batchSelectedSuggestionIds,
  handleApplySuggestion,
  handleApplyBatch,
  handleDismissSuggestion,
  appointmentTimezones,
  tmsName,
}: SuggestionListProps) {
  // At most one Preview tooltip open across all Kit suggestions.
  const [openPreviewSuggestionId, setOpenPreviewSuggestionId] =
    useState<Maybe<number>>(null);

  // Close Preview when the pointer enters a different suggestion row
  //  (unless that row immediately re-opens its own Preview on the info icon).
  const handleSuggestionRowPointerEnter = (suggestionId: number) => {
    setOpenPreviewSuggestionId((current) =>
      current != null && current !== suggestionId ? null : current
    );
  };

  return (
    <Flex direction='col' gap='sm' className='w-full'>
      {pipelinesToShow.map((pipelineEnum) => (
        <SuggestionCategory
          key={pipelineEnum}
          pipelineEnum={pipelineEnum}
          additionalSuggestions={
            pipelineEnum === SuggestionPipelines.LoadBuilding
              ? additionalLoadBuildingSuggestions
              : undefined
          }
          suggestions={groupedSuggestions[pipelineEnum]}
          clickedSuggestion={clickedSuggestion}
          expandedPipeline={expandedPipeline}
          setExpandedPipeline={setExpandedPipeline}
          visibleCountByPipeline={visibleCountByPipeline}
          setVisibleCountByPipeline={setVisibleCountByPipeline}
          isBatchQuoteEnabled={isBatchQuoteEnabled}
          batchSelectedSuggestionIds={batchSelectedSuggestionIds}
          openPreviewSuggestionId={openPreviewSuggestionId}
          setOpenPreviewSuggestionId={setOpenPreviewSuggestionId}
          onSuggestionRowPointerEnter={handleSuggestionRowPointerEnter}
          handleApplySuggestion={handleApplySuggestion}
          handleApplyBatch={handleApplyBatch}
          handleDismissSuggestion={handleDismissSuggestion}
          appointmentTimezones={appointmentTimezones}
          tmsName={tmsName}
        />
      ))}
    </Flex>
  );
}
